import { useEffect, useRef, useState } from 'react'
import { useLanguage, useMessages } from '@/features/language/use-language'
import { IconButton } from '@/shared/ui/IconButton'
import { Notice } from '@/shared/ui/Notice'
import { OutlineButton } from '@/shared/ui/OutlineButton'
import { CloseIcon } from '@/shared/ui/icons'
import { useSuggestNickname, useUpdateNickname } from './queries'
import { NICKNAME_MAX_LENGTH, countGraphemes, normalizeNickname } from './types'

interface NicknameSheetProps {
  current: string
  onSaved: () => void
  onClose: () => void
}

/**
 * 닉네임을 고치는 자리.
 *
 * 설정 안에 입력 필드를 바로 두지 않고 이 자리를 연다. 목록 가운데 입력 상자가 서면 그 행만
 * 테두리를 갖고, 상시로 놓인 필드는 스크롤하다 눌려 키보드를 올린다. 고치는 데 필요한 것도
 * 필드 하나가 아니다.
 *
 * 저장 버튼을 둔다. 자동저장이 지키는 것은 쌓이는 값이고 닉네임은 덮어쓰는 값 하나다. 비어
 * 있거나 상한을 넘는 저장할 수 없는 상태가 있는데 자동저장에는 그것을 다룰 자리가 없고,
 * 자동저장의 리듬을 걸면 지우고 다시 쓰는 도중의 값이 계정에 남는다.
 *
 * 바꾼 값을 두고 닫으면 그 값을 버리고 되묻지 않는다. 잃는 것이 방금 친 몇 글자라 확인
 * 대화가 그보다 무겁다.
 */
export function NicknameSheet({ current, onSaved, onClose }: NicknameSheetProps) {
  const m = useMessages()
  const { languageId } = useLanguage()
  const [draft, setDraft] = useState(current)
  const field = useRef<HTMLInputElement>(null)

  const save = useUpdateNickname()
  const suggest = useSuggestNickname()

  useEffect(() => {
    field.current?.focus()
    field.current?.select()
  }, [])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  const length = countGraphemes(draft.trim())
  const over = length - NICKNAME_MAX_LENGTH
  const empty = normalizeNickname(draft).length === 0
  const busy = save.isPending || suggest.isPending
  const savable = !empty && over <= 0 && !busy

  const submit = () => {
    if (!savable) return
    save.mutate(normalizeNickname(draft), { onSuccess: onSaved })
  }

  return (
    <div className="fixed inset-0 z-20 flex items-end justify-center sm:items-center">
      <div onClick={onClose} aria-hidden="true" className="absolute inset-0 bg-black/30" />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="nickname-sheet-title"
        className="bg-surface rounded-card relative w-full max-w-md px-5 pt-5 pb-7 shadow-lg sm:pb-5"
      >
        <div className="-mr-2.5 flex items-start justify-between gap-4">
          <h2 id="nickname-sheet-title" className="text-lg font-medium">
            {m.settings.nickname.sheetTitle}
          </h2>
          <IconButton label={m.settings.nickname.close} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>

        {/* 안심 문장. 닉네임이라는 낱말이 남에게 보이는 것을 뜻해 온 말이라 이 앱에서도
            누군가 본다고 읽힌다. 적으면 안 되는 것을 나열하지 않고 결로 전한다. */}
        <p className="text-ink-muted mt-1.5 text-sm leading-[1.6]">
          {m.settings.nickname.sheetHint}
        </p>

        <form
          className="mt-4 flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault()
            submit()
          }}
        >
          {/* 자동완성을 붙이지 않는다. 이름이나 사용자명으로 힌트를 주면 브라우저가 실명을
              채워 넣어 사용자가 뜻하지 않은 값이 들어온다. */}
          <input
            ref={field}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            aria-label={m.settings.nickname.label}
            aria-describedby="nickname-counter"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="done"
            disabled={busy}
            className="border-ink-muted rounded-card text-ink focus-visible:ring-ink focus-visible:ring-offset-surface min-h-11 border bg-transparent px-3.5 text-base outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-60"
          />

          <p id="nickname-counter" aria-live="polite" className="text-ink-muted min-h-5 text-sm">
            {over > 0
              ? m.settings.nickname.over(over)
              : m.settings.nickname.remaining(NICKNAME_MAX_LENGTH - length)}
          </p>

          {save.isError && <Notice>{m.settings.nickname.saveFailed}</Notice>}
          {suggest.isError && <Notice>{m.settings.nickname.regenerateFailed}</Notice>}

          <div className="flex items-center justify-between gap-3">
            <OutlineButton
              onClick={() =>
                suggest.mutate(languageId, { onSuccess: (next) => setDraft(next.nickname) })
              }
              disabled={busy}
            >
              {suggest.isPending
                ? m.settings.nickname.regenerating
                : m.settings.nickname.regenerate}
            </OutlineButton>

            <button
              type="submit"
              disabled={!savable}
              className="pressable bg-accent text-accent-ink border-accent-ink rounded-card min-h-11 border px-[18px] text-[15px] font-medium disabled:opacity-60"
            >
              {save.isPending ? m.settings.nickname.saving : m.settings.nickname.save}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
